/**
 * Modal — thin wrapper around shadcn Dialog matching the legacy @jn/ui Modal API.
 * Props: open, onClose, title, footer?, children, size?
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';

export interface ModalProps {
  open:      boolean;
  onClose:   () => void;
  title?:    string;
  footer?:   React.ReactNode;
  children:  React.ReactNode;
  className?: string;
  size?:     'sm' | 'md' | 'lg' | 'xl' | string;
}

const SIZE_CLASS: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, footer, children, className, size = 'md' }: ModalProps) {
  const sizeClass = SIZE_CLASS[size] ?? (typeof size === 'string' && size.startsWith('max-w-') ? size : undefined);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className={cn(
          'grid-cols-1 !flex max-h-[min(90vh,880px)] flex-col gap-4 overflow-hidden',
          sizeClass,
          className,
        )}
      >
        {title && (
          <DialogHeader className="shrink-0 pr-8">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
        {footer && <DialogFooter className="shrink-0">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
